import { useMemo, useState } from "react";
import type { FieldValues } from "react-hook-form";
import {
  Form,
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCheck,
  Clock3,
  LoaderCircle,
  Mail,
  RotateCcw,
  UserRoundCheck,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../providers/types";
import type {
  ContactApproval,
  ContactApprovalContactData,
  ContactApprovalReviewResult,
} from "../types";
import { ContactInputs } from "./ContactInputs";
import { useM_ContactApproval_Review } from "./useM_ContactApproval_Review";

const PENDING_LIMIT = 50;

const buildApprovalContactData = (
  values: ContactApprovalContactData,
): ContactApprovalContactData => ({
  background: values.background,
  company_id: values.company_id || null,
  email: values.email,
  first_name: values.first_name,
  gender: values.gender,
  last_name: values.last_name,
  linkedin_url: values.linkedin_url,
  phone: values.phone,
  record_type: values.record_type,
  sales_id: values.sales_id,
  tags: values.tags ?? [],
  title: values.title,
});

export const ContactApprovalInbox = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [selectedApproval, setSelectedApproval] =
    useState<ContactApproval | null>(null);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const pendingQuery = useGetList<ContactApproval>("contact_approvals", {
    filter: { "status@eq": "pending" },
    pagination: { page: 1, perPage: PENDING_LIMIT },
    sort: { field: "created_at", order: "DESC" },
  });
  const pending = pendingQuery.data ?? [];

  const handleReviewed = (result: ContactApprovalReviewResult) => {
    const next = pending.find((approval) => approval.id !== result.approval_id);
    setSelectedApproval(next ?? null);
  };

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    let ok = 0;
    let failed = 0;
    for (const approval of pending) {
      try {
        await dataProvider.reviewContactApproval(
          approval.id,
          "approve",
          getDefaultApprovalContactData(approval, identity?.id),
        );
        ok++;
      } catch {
        failed++;
      }
    }
    setBulkApproving(false);
    setBulkApproveOpen(false);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["contact_approvals"] }),
      queryClient.invalidateQueries({ queryKey: ["contacts"] }),
    ]);
    notify(
      failed === 0
        ? "resources.contacts.approvals.notifications.bulk_approved"
        : "resources.contacts.approvals.notifications.bulk_partial",
      {
        type: failed === 0 ? "success" : "warning",
        messageArgs: failed === 0 ? { smart_count: ok } : { ok, failed },
      },
    );
  };

  return (
    <>
      <Card
        className={cn(
          "mx-2 mt-2 gap-0 overflow-hidden py-0 shrink-0",
          pending.length
            ? "border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/25"
            : "bg-muted/25",
        )}
      >
        <div className="flex min-h-12 items-center gap-3 px-3 py-2">
          <div
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-full",
              pending.length
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300"
                : "bg-muted text-muted-foreground",
            )}
          >
            <UserRoundCheck className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">
                {translate("resources.contacts.approvals.title")}
              </span>
              <Badge
                variant={pending.length ? "default" : "secondary"}
                className={cn(
                  "h-5 min-w-5 rounded-full px-1.5",
                  pending.length &&
                    "bg-amber-500 text-white hover:bg-amber-500 dark:bg-amber-600",
                )}
              >
                {pendingQuery.total ?? pending.length}
              </Badge>
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {translate("resources.contacts.approvals.description")}
            </p>
          </div>
          {pending.length > 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={bulkApproving}
              onClick={() => setBulkApproveOpen(true)}
              className="shrink-0"
            >
              {bulkApproving ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <CheckCheck className="size-4" />
              )}
              <span className="hidden sm:inline">
                {translate("resources.contacts.approvals.approve_all")}
              </span>
            </Button>
          )}
        </div>

        {pendingQuery.isPending ? (
          <div className="space-y-2 border-t px-3 py-3">
            <Skeleton className="h-11 w-full" />
          </div>
        ) : pendingQuery.error ? (
          <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-sm text-destructive">
            <span>
              {translate("resources.contacts.approvals.loading_error")}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => pendingQuery.refetch()}
            >
              <RotateCcw className="size-4" />
              {translate("crm.common.retry")}
            </Button>
          </div>
        ) : pending.length ? (
          <ScrollArea
            className="border-t"
            style={{ height: Math.min(pending.length, 6) * 60 }}
          >
            <div className="divide-y divide-amber-200/70 dark:divide-amber-900/70">
              {pending.map((approval) => (
                <PendingApprovalRow
                  key={approval.id}
                  approval={approval}
                  onReview={setSelectedApproval}
                  onReviewed={handleReviewed}
                />
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex items-center justify-center border-t px-3 py-4 text-xs text-muted-foreground">
            <UserRoundCheck className="size-4 mr-2 opacity-50" />
            {translate("resources.contacts.approvals.no_pending")}
          </div>
        )}
      </Card>

      <Dialog open={bulkApproveOpen} onOpenChange={setBulkApproveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.contacts.approvals.approve_all_title")}
            </DialogTitle>
            <DialogDescription>
              {translate(
                "resources.contacts.approvals.approve_all_description",
                { smart_count: pending.length },
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-48 overflow-y-auto border rounded-md divide-y text-sm">
            {pending.map((approval) => (
              <div
                key={approval.id}
                className="flex items-center gap-2 px-3 py-1.5"
              >
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold">
                  {getInitials(approval)}
                </span>
                <span className="truncate">{getApprovalName(approval)}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={bulkApproving}
              onClick={() => setBulkApproveOpen(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="button"
              disabled={bulkApproving}
              onClick={handleBulkApprove}
            >
              {bulkApproving && (
                <LoaderCircle className="size-4 animate-spin" />
              )}
              {translate(
                bulkApproving
                  ? "resources.contacts.approvals.approving"
                  : "resources.contacts.approvals.approve_all_confirm",
                { smart_count: pending.length },
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContactApprovalDialog
        approval={selectedApproval}
        onChangedState={() => {
          setSelectedApproval(null);
        }}
        onOpenChange={(open) => {
          if (!open) setSelectedApproval(null);
        }}
        onReviewed={handleReviewed}
      />
    </>
  );
};

const PendingApprovalRow = ({
  approval,
  onReview,
  onReviewed,
}: {
  approval: ContactApproval;
  onReview: (approval: ContactApproval) => void;
  onReviewed: (result: ContactApprovalReviewResult) => void;
}) => {
  const [locale = "en"] = useLocaleState();
  const name = getApprovalName(approval);
  const isEmailFallback = !approval.first_name && !approval.last_name;

  return (
    <div className="flex items-center gap-2 px-3 py-2.5 transition-colors hover:bg-amber-100/70 dark:hover:bg-amber-900/30">
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        onClick={() => onReview(approval)}
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-background text-sm font-semibold shadow-xs">
          {getInitials(approval)}
        </div>
        <div className="min-w-0 flex-1">
          <span
            className={cn(
              "truncate text-sm font-medium",
              isEmailFallback && "text-muted-foreground",
            )}
          >
            {name}
          </span>
          <div className="flex min-w-0 items-center gap-3 text-xs text-muted-foreground">
            <span className="flex min-w-0 items-center gap-1 truncate">
              <Mail className="size-3 shrink-0" />
              {approval.normalized_email}
            </span>
            <span className="hidden items-center gap-1 sm:flex shrink-0">
              <Clock3 className="size-3" />
              {formatDate(approval.created_at, locale)}
            </span>
          </div>
        </div>
      </button>
      <QuickReviewActions approval={approval} onReviewed={onReviewed} />
    </div>
  );
};

const QuickReviewActions = ({
  approval,
  onReviewed,
}: {
  approval: ContactApproval;
  onReviewed: (result: ContactApprovalReviewResult) => void;
}) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [rejectOpen, setRejectOpen] = useState(false);
  const mReview = useM_ContactApproval_Review({
    approvalId: Number(approval.id),
    onSuccess: (result) => {
      setRejectOpen(false);
      onReviewed(result);
    },
  });
  const isPending = mReview.mutation.isPending;

  const handleApprove = async () => {
    try {
      await mReview.mutation.mutateAsync({
        contact: getDefaultApprovalContactData(approval, identity?.id),
        decision: "approve",
      });
    } catch {
      // Mutation feedback is centralized in the hook.
    }
  };

  const handleReject = async () => {
    try {
      await mReview.mutation.mutateAsync({ decision: "reject" });
    } catch {
      // Mutation feedback is centralized in the hook.
    }
  };

  return (
    <>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="sm"
          disabled={isPending || !identity}
          onClick={handleApprove}
        >
          {isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          <span className="hidden sm:inline">
            {translate("resources.contacts.approvals.approve")}
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={() => setRejectOpen(true)}
        >
          <X className="size-4" />
          <span className="hidden sm:inline">
            {translate("resources.contacts.approvals.reject")}
          </span>
        </Button>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("resources.contacts.approvals.reject_title")}
            </DialogTitle>
            <DialogDescription>
              {translate("resources.contacts.approvals.reject_description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={isPending}
              onClick={() => setRejectOpen(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isPending}
              onClick={handleReject}
            >
              {isPending && <LoaderCircle className="size-4 animate-spin" />}
              {translate(
                isPending
                  ? "resources.contacts.approvals.rejecting"
                  : "resources.contacts.approvals.reject",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ContactApprovalDialog = ({
  approval,
  onChangedState,
  onOpenChange,
  onReviewed,
}: {
  approval: ContactApproval | null;
  onChangedState: () => void;
  onOpenChange: (open: boolean) => void;
  onReviewed: (result: ContactApprovalReviewResult) => void;
}) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [rejectOpen, setRejectOpen] = useState(false);
  const approvalId = approval == null ? 0 : Number(approval.id);
  const mReview = useM_ContactApproval_Review({
    approvalId,
    onChangedState,
    onSuccess: (result) => {
      setRejectOpen(false);
      onReviewed(result);
    },
  });
  const defaultValues = useMemo<ContactApprovalContactData>(
    () => getDefaultApprovalContactData(approval, identity?.id),
    [approval, identity?.id],
  );

  if (!approval) return null;

  // ra-core's Form types onSubmit as SubmitHandler<FieldValues>, so the parameter has
  // to be the wide type and narrow here. Declaring it as the narrow one was backwards:
  // it promised the Form would only ever hand over a well-formed record.
  const handleApprove = async (values: FieldValues) => {
    const contactValues = values as ContactApprovalContactData;
    try {
      await mReview.mutation.mutateAsync({
        contact: buildApprovalContactData(contactValues),
        decision: "approve",
      });
    } catch {
      // Mutation feedback is centralized in the hook.
    }
  };

  const handleReject = async () => {
    try {
      await mReview.mutation.mutateAsync({ decision: "reject" });
    } catch {
      // Mutation feedback is centralized in the hook.
    }
  };

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
          <Form
            key={`${approval.id}-${identity?.id ?? "loading"}`}
            defaultValues={defaultValues}
            onSubmit={handleApprove}
            className="flex min-h-0 flex-1 flex-col"
          >
            <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
              <DialogTitle>
                {translate("resources.contacts.approvals.review_contact")}
              </DialogTitle>
              <DialogDescription>{approval.normalized_email}</DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
                <ContactInputs />
              </div>
            </div>

            <DialogFooter className="shrink-0 border-t bg-background">
              <div className="mx-auto flex w-full max-w-5xl gap-3">
                <Button
                  type="button"
                  variant="destructive"
                  className="flex-1"
                  disabled={mReview.mutation.isPending}
                  onClick={() => setRejectOpen(true)}
                >
                  {mReview.mutation.isPending ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <X className="size-4" />
                  )}
                  {translate("resources.contacts.approvals.reject")}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={mReview.mutation.isPending || !identity}
                >
                  {mReview.mutation.isPending ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <Check className="size-4" />
                  )}
                  {translate(
                    mReview.mutation.isPending
                      ? "resources.contacts.approvals.approving"
                      : "resources.contacts.approvals.approve",
                  )}
                </Button>
              </div>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("resources.contacts.approvals.reject_title")}
            </DialogTitle>
            <DialogDescription>
              {translate("resources.contacts.approvals.reject_description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={mReview.mutation.isPending}
              onClick={() => setRejectOpen(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={mReview.mutation.isPending}
              onClick={handleReject}
            >
              {mReview.mutation.isPending && (
                <LoaderCircle className="size-4 animate-spin" />
              )}
              {translate(
                mReview.mutation.isPending
                  ? "resources.contacts.approvals.rejecting"
                  : "resources.contacts.approvals.reject",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const getDefaultApprovalContactData = (
  approval: ContactApproval | null,
  // Identifier, not number: every caller passes identity?.id, and the sales_id it
  // lands in is Identifier on Contact.
  salesId?: Identifier,
): ContactApprovalContactData => ({
  background: "",
  company_id: null,
  email: approval?.normalized_email ?? "",
  first_name: approval?.first_name ?? "",
  gender: "male",
  last_name: approval?.last_name ?? "",
  linkedin_url: null,
  phone: "",
  record_type: "network",
  sales_id: salesId,
  tags: [],
  title: "",
});

const getApprovalName = (approval: ContactApproval) =>
  [approval.first_name, approval.last_name].filter(Boolean).join(" ") ||
  approval.normalized_email;

const getInitials = (approval: ContactApproval) => {
  const initials = `${approval.first_name?.[0] ?? ""}${approval.last_name?.[0] ?? ""}`;
  if (initials) return initials.toUpperCase();
  // Fallback: first character of the email local-part
  const local = (approval.normalized_email ?? "").split("@")[0];
  return local ? local[0].toUpperCase() : "?";
};

const formatDate = (value: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
